import { getExportFile, startExport } from "@api/nonModelledElementsExport";
import { EditMode } from "@/types/editMode";
import { ModelCommandResponse } from "@api/apiClient";
import i18n from "@i18n/i18n";
import { Button, Container, Grid, Snackbar, Typography } from "@mui/material";
import { observer } from "mobx-react";
import { useState } from "react";
import NonModelledElementDialog from "./NonModelledElementDialog";
import NonModelledElementsGrid from "./NonModelledElementsGrid";

const POLL_INTERVAL_MS = 2000;
const POLL_MAX_ATTEMPTS = 60;

const NonModelledElementsPage = observer(() => {
  const [mode, setMode] = useState<EditMode>(EditMode.Add);
  const [data, setData] = useState<ModelCommandResponse>({ name: "Basic" } as ModelCommandResponse);
  const [openRebarDialog, setOpenRebarDialog] = useState<boolean>(false);
  const [exporting, setExporting] = useState(false);
  const [snackMessage, setSnackMessage] = useState<string | null>(null);

  function handleviewRebar() {
    setOpenRebarDialog(true);
    setMode(EditMode.Add);
  }

  function handleviewnonElementConfig(commadObje: ModelCommandResponse) {
    setMode(EditMode.Edit);
    setData(commadObje);
    setOpenRebarDialog(true);
  }

  const handleCloseRebarSettings = () => {
    setOpenRebarDialog(false);
  };

  const handleExport = async () => {
    setExporting(true);
    setSnackMessage(null);
    try {
      const { jobId } = await startExport();
      if (!jobId) {
        setSnackMessage(i18n.t("non-model-element.non-model-element-grid.export-error"));
        return;
      }
      let attempts = 0;
      while (attempts < POLL_MAX_ATTEMPTS) {
        await new Promise((r) => setTimeout(r, POLL_INTERVAL_MS));
        const result = await getExportFile(jobId);
        if (result) {
          const url = URL.createObjectURL(result.blob);
          const a = document.createElement("a");
          a.href = url;
          a.download = result.filename;
          a.click();
          URL.revokeObjectURL(url);
          setSnackMessage(i18n.t("non-model-element.non-model-element-grid.export-success"));
          break;
        }
        attempts++;
      }
      if (attempts >= POLL_MAX_ATTEMPTS) {
        setSnackMessage(i18n.t("non-model-element.non-model-element-grid.export-timeout"));
      }
    } catch (e) {
      setSnackMessage(i18n.t("non-model-element.non-model-element-grid.export-error"));
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <Container maxWidth={false} sx={{ height: "100%" }}>
        <Grid mt={2} container spacing={2} sx={{ display: "flex", flexDirection: "column", height: "100%" }}>
          <Grid size={12} sx={{ display: "flex", flexGrow: 0 }}>
            <Typography variant="h6" fontWeight="bold">
              {i18n.t("non-model-element.header")}
            </Typography>
          </Grid>
          <Grid size={12} sx={{ display: "flex", flexGrow: 0, gap: 1, flexWrap: "wrap" }}>
            <Button variant="outlined" onClick={() => handleviewRebar()}>
              {i18n.t("non-model-element.add-Configuration-button")}
            </Button>
            <Button variant="outlined" onClick={handleExport} disabled={exporting}>
              {exporting ? i18n.t("non-model-element.non-model-element-grid.exporting") : i18n.t("non-model-element.non-model-element-grid.export-tooltip")}
            </Button>
          </Grid>
          <Grid size={12} sx={{ display: "flex", flexGrow: 1, flexDirection: "column", position: "relative" }}>
            <NonModelledElementsGrid onEdit={(commandObject: ModelCommandResponse) => handleviewnonElementConfig(commandObject)} />
          </Grid>
        </Grid>
      </Container>
      {openRebarDialog && (
        <NonModelledElementDialog
          open={openRebarDialog}
          editMode={mode}
          onClose={() => handleCloseRebarSettings()}
          filteredElementConfigurationId={""}
          data={data}
        ></NonModelledElementDialog>
      )}
      <Snackbar
        open={!!snackMessage}
        autoHideDuration={4000}
        anchorOrigin={{ vertical: "top", horizontal: "right" }}
        message={snackMessage ?? ""}
        onClose={() => setSnackMessage(null)}
      />
    </>
  );
});

export default NonModelledElementsPage;
